import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import InputField from "@/components/form/InputField";
import {
  buildCreatorJobsQueryParams,
  useGetCreatorJobsQuery,
} from "@/redux/feature/jobs/jobsApi";
import JobsCard from "./components/JobsCard";
import {
  TJob,
  TJobsResponse,
  TOrderVideo,
} from "@/redux/feature/jobs/jobsType";
import { useAppSelector } from "@/redux/hooks/hooks";
import BlurPopUp from "./components/BlurPopUp";
import Modal from "@/components/ui/Modal";
import { ArrowDownUp, Check, ChevronDown, RefreshCw, X } from "lucide-react";
import { useCreatorStatus } from "@/hooks/useCreatorStatuts";
import usePolling from "@/hooks/usePolling";
import { JobsCardLoadingPlaceholder } from "./components/JobsCardLoadingPlaceholder";
import Pagination from "@/components/ui/Pagination";
import IncompleteApplicationReminderBanner from "../dashboard/components/IncompleteApplicationReminderBanner";
import ProjectConfirmModal from "./components/ProjectConfirmModal";
import ProjectConfirmationModal from "./components/ProjectConfirmationModal";
import SameDayAvailabilityCard from "./components/SameDayAvailabilityCard";
import { AvailabilityModal } from "../sameDayDelivery/AvailabilityModal";

export type TFilterOptions = "available" | "applied" | "invited" | "same_day";
type TSortDirection = "desc" | "asc";

const JOBS_PER_PAGE = 10;
const APPLIED_JOBS_SORT_FETCH_LIMIT = 10_000;

const VALID_FILTERS = new Set<TFilterOptions>([
  "available",
  "applied",
  "invited",
  "same_day",
]);

const parseFilterParam = (value: string | null): TFilterOptions => {
  if (value && VALID_FILTERS.has(value as TFilterOptions)) {
    return value as TFilterOptions;
  }
  return "available";
};
const FilterOptions = [
  {
    title: "Invited Jobs",
    value: "invited",
  },
  {
    title: "Available Jobs",
    value: "available",
  },
  {
    title: "Same-day Jobs",
    value: "same_day",
  },
  {
    title: "Applied",
    value: "applied",
  },
];
const SortControl = ({
  value,
  onChange,
}: {
  value: TSortDirection;
  onChange: (value: TSortDirection) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const options: {
    value: TSortDirection;
    label: string;
    description: string;
  }[] = [
    {
      value: "desc",
      label: "Newest first",
      description: "Recently posted jobs first",
    },
    {
      value: "asc",
      label: "Oldest first",
      description: "Earlier posted jobs first",
    },
  ];

  useEffect(() => {
    if (!isOpen) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isOpen]);

  const selectedLabel =
    options.find((option) => option.value === value)?.label ?? "Newest first";

  return (
    <div ref={containerRef} className="relative w-[190px]">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
        className={`flex h-10 w-full items-center gap-2 rounded-xl border bg-white px-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.05)] transition-all ${
          isOpen
            ? "border-[#17457D] ring-2 ring-[#17457D]/10"
            : "border-[#D0D5DD] hover:border-[#98A2B3]"
        }`}
      >
        <span className="flex size-6 items-center justify-center rounded-md bg-[#17457D0F]">
          <ArrowDownUp className="size-3.5 text-[#17457D]" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-medium leading-3 text-[#98A2B3]">
            Sort by
          </span>
          <span className="block truncate text-xs font-semibold leading-4 text-[#344054]">
            {selectedLabel}
          </span>
        </span>
        <ChevronDown
          className={`size-4 shrink-0 text-[#667085] transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-label="Sort applied jobs"
          className="absolute left-0 z-50 mt-2 w-[230px] overflow-hidden rounded-xl border border-[#E4E7EC] bg-white p-1.5 shadow-[0_12px_32px_rgba(16,24,40,0.14)] md:left-auto md:right-0"
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                  isSelected ? "bg-[#17457D0F]" : "hover:bg-[#F9FAFB]"
                }`}
              >
                <span className="min-w-0 flex-1">
                  <span
                    className={`block text-sm font-medium ${
                      isSelected ? "text-[#17457D]" : "text-[#344054]"
                    }`}
                  >
                    {option.label}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-[#98A2B3]">
                    {option.description}
                  </span>
                </span>
                <span
                  className={`flex size-5 shrink-0 items-center justify-center rounded-full ${
                    isSelected ? "bg-[#17457D] text-white" : "text-transparent"
                  }`}
                >
                  <Check className="size-3" strokeWidth={3} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Jobs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pinnedOrderId = searchParams.get("order_id") ?? undefined;
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<TFilterOptions>(() =>
    parseFilterParam(searchParams.get("filter")),
  );
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortDirection, setSortDirection] = useState<TSortDirection>("desc");
  const [recentlyAppliedOrderId, setRecentlyAppliedOrderId] = useState<
    string | null
  >(null);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [isProjectConfirmModalOpen, setIsProjectConfirmModalOpen] =
    useState(true); // Represents the videos currently shown in details view.
  const [visibleVideos, setVisibleVideos] = useState<TOrderVideo[]>([]); // Represents the videos currently shown in details view.
  const [isVideoDetailsModalOpen, setIsVideoDetailsModalOpen] = useState(false);
  const [showSameDayAvailabilityModal, setShowSameDayAvailabilityModal] =
    useState(false);
  // Get user email from Redux state
  const userEmail = useAppSelector((state) => state?.auth?.user?.email);

  const {
    shouldShowPopup,
    status,
    isLoading: isStatusLoading,
    isError: isStatusError,
  } = useCreatorStatus(userEmail as string);

  const renderPopup = () => {
    if (shouldShowPopup) {
      return <BlurPopUp status={status} />;
    }
    return null;
  };
  // Create polling intervals
  const jobRefreshPollingInterval = usePolling({
    templateIds: [
      "new_project_available",
      "creator_matching_orders_found",
      "creator_matching_orders_batch",
      "creator_application_not_selected",
      "creator_application_still_in_consideration",
      "creator_unassigned_from_project",
      "creator_project_selection",
    ],
  }).pollingInterval;

  const shouldSkipJobs =
    !userEmail ||
    isStatusError ||
    shouldShowPopup ||
    (status?.isNotInitiated && !isStatusLoading);
  const isAppliedJobsView = filter === "applied";

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [filter, debouncedSearch]);

  useEffect(() => {
    const nextFilter = parseFilterParam(searchParams.get("filter"));
    setFilter((current) => (current === nextFilter ? current : nextFilter));
  }, [searchParams]);

  const queryParams = useMemo(
    () =>
      buildCreatorJobsQueryParams({
        email: userEmail,
        sort: filter,
        // Applied jobs are sorted in the client. Request their full result set
        // before slicing it into pages so the order is global, not per page.
        page: isAppliedJobsView ? 1 : page,
        limit: isAppliedJobsView ? APPLIED_JOBS_SORT_FETCH_LIMIT : JOBS_PER_PAGE,
        pinOrderId: pinnedOrderId,
        search: debouncedSearch || undefined,
        refresh: refreshNonce > 0 ? refreshNonce : undefined,
      }),
    [
      userEmail,
      filter,
      page,
      debouncedSearch,
      pinnedOrderId,
      refreshNonce,
      isAppliedJobsView,
    ],
  );

  const {
    data: jobsResponse,
    currentData: currentJobsResponse,
    isLoading,
    isFetching,
    error,
    isError,
  } = useGetCreatorJobsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
    pollingInterval: jobRefreshPollingInterval,
    skip: shouldSkipJobs,
  });

  const jobsData = jobsResponse?.data?.data;
  const jobsDataMeta = jobsResponse?.data?.meta;
  const isSearchPending = searchInput.trim() !== debouncedSearch;

  useEffect(() => {
    if (!isFetching) {
      setIsPageTransitioning(false);
      setIsRefreshing(false);
    }
  }, [isFetching]);

  const handlePageChange: Dispatch<SetStateAction<number>> = (
    nextPage,
  ) => {
    setPage(nextPage);
    // Applied jobs are already loaded and paginated locally after sorting.
    // Starting the request-loading state here would leave the skeleton visible.
    setIsPageTransitioning(!isAppliedJobsView);
  };

  const sortedJobsData = useMemo(() => {
    const jobs = [...(jobsData ?? [])];

    return jobs.sort((a, b) => {
      if (filter === "applied" && sortDirection === "desc") {
        if (a.orderDetails.order_id === recentlyAppliedOrderId) return -1;
        if (b.orderDetails.order_id === recentlyAppliedOrderId) return 1;
      }

      const aTime = new Date(a.orderDetails.postedAt).getTime() || 0;
      const bTime = new Date(b.orderDetails.postedAt).getTime() || 0;
      return sortDirection === "desc" ? bTime - aTime : aTime - bTime;
    });
  }, [filter, jobsData, recentlyAppliedOrderId, sortDirection]);

  const jobsForCurrentPage = useMemo(() => {
    if (!isAppliedJobsView) return sortedJobsData;

    const start = (page - 1) * JOBS_PER_PAGE;
    return sortedJobsData.slice(start, start + JOBS_PER_PAGE);
  }, [isAppliedJobsView, page, sortedJobsData]);

  const totalPages = isAppliedJobsView
    ? Math.ceil((jobsDataMeta?.total ?? sortedJobsData.length) / JOBS_PER_PAGE)
    : jobsDataMeta?.totalPages;

  const handleFilterChange = (nextFilter: TFilterOptions) => {
    setFilter(nextFilter);
    setPage(1);
    setRefreshNonce(0);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("filter", nextFilter);
    setSearchParams(nextParams, { replace: true });
  };

  const handleRefreshJobs = () => {
    setPage(1);
    setIsRefreshing(true);
    setRefreshNonce(Date.now());
  };

  const handleJobApplied = (orderId: string) => {
    setRecentlyAppliedOrderId(orderId);
    setSortDirection("desc");
    setPage(1);
    setFilter("applied");
  };

  const sectionDescription =
    filter === "applied"
      ? "Jobs you have applied to"
      : filter === "invited"
        ? "Jobs you have been invited to"
        : filter === "same_day"
          ? "Same-day delivery jobs open for you"
          : "Available jobs for you";

  const emptyStateMessage = debouncedSearch
    ? "No jobs match your search."
    : filter === "applied"
      ? "You haven't applied to any jobs yet."
      : filter === "invited"
        ? "You don't have any job invitations yet."
        : filter === "same_day"
          ? "No same-day jobs are available for you right now."
          : "No jobs are available for you.";
  const showInitialLoading =
    isLoading ||
    isSearchPending ||
    isPageTransitioning ||
    (isFetching && !currentJobsResponse);
  const getButtonClassName = (value: string) => {
    const baseClasses =
      "flex items-center rounded-[32px] border border-[#E4E7EC] px-4 py-2.5";
    return filter === value ? `${baseClasses} bg-[#F2F4F7]` : baseClasses;
  };

  if (error) {
    return <div>Error loading jobs</div>;
  }

  return (
    <div className="relative px-4 pt-4 md:px-11 md:mb-8 mb-28">
      {status?.isNotInitiated && <IncompleteApplicationReminderBanner />}
      {/* Popup container - positioned relative to the main content area */}
      {shouldShowPopup && (
        <div className="absolute w-full h-screen top-0 left-0  backdrop-blur-sm z-20 flex justify-center items-center">
          <div className=" rounded-xl shadow-lg max-w-lg w-full mx-auto">
            {renderPopup()}
          </div>
        </div>
      )}

      {/* same day banner */}
      {filter === "same_day" && (
        <div className="mb-4">
          <SameDayAvailabilityCard
            setShowModifySlot={() => setShowSameDayAvailabilityModal(true)}
          />
        </div>
      )}

      {/* Main content container */}

      {isError || isStatusError ? (
        <>
          <p className="py-20 flex justify-center items-center font-semibold animate-pulse">
            Some thing Went Wrong...
          </p>
        </>
      ) : (
        <div className={`${shouldShowPopup ? "filter blur-sm" : "pt-5"}`}>
          <div className="mb-6 flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg leading-6 tracking-[-2.5%] font-semibold">
                {showInitialLoading ? (
                  <span className="text-[#98A2B3] text-sm animate-pulse">
                    Loading jobs...
                  </span>
                ) : (
                  <>
                    {jobsDataMeta?.total ?? 0}{" "}
                    {jobsDataMeta?.total === 1 ? "Job" : "Jobs"}
                  </>
                )}
              </h3>
              <p className="text-[#98A2B3] text-xs leading-5 tracking-[-2%] font-medium">
                {sectionDescription}
              </p>
            </div>
            <button
              type="button"
              onClick={handleRefreshJobs}
              disabled={shouldSkipJobs || isRefreshing}
              title="Refresh jobs (bypass cache)"
              aria-label="Refresh jobs"
              className="flex shrink-0 items-center gap-2 rounded-[32px] border border-[#E4E7EC] px-3 py-2 text-sm font-medium text-[#667085] transition-colors hover:bg-[#F2F4F7] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
          <div>
            <div className="flex w-full flex-col gap-3">
              <div className="flex w-full flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-x-3 overflow-x-auto pb-1 md:overflow-visible md:pb-0">
                  {FilterOptions.map((item) => (
                    <button
                      onClick={() =>
                        handleFilterChange(item.value as TFilterOptions)
                      }
                      key={item.value}
                      className={getButtonClassName(item.value)}
                    >
                      <h5 className="text-sm leading-5 whitespace-nowrap font-medium">
                        <span className="text-[#667085] ">{item.title}</span>{" "}
                      </h5>
                    </button>
                  ))}
                </div>

                {filter === "applied" && (
                  <SortControl
                    value={sortDirection}
                    onChange={(nextDirection) => {
                      setSortDirection(nextDirection);
                      setPage(1);
                    }}
                  />
                )}
              </div>

              <InputField
                name="jobSearch"
                variant="search"
                placeholder="Search by project, video, company, or client..."
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                className="max-w-full md:max-w-[360px]"
              />
            </div>
          </div>
          <div>
            {showInitialLoading ? (
              <div className="mt-10 space-y-4">
                {Array.from({ length: 8 }, (_, index) => (
                  <JobsCardLoadingPlaceholder key={index} />
                ))}
              </div>
            ) : (
              <div className="mt-10 space-y-4">
                {jobsForCurrentPage.map((job: TJob) => (
                  <JobsCard
                    filter={filter}
                    onApplied={handleJobApplied}
                    setVisibleVideos={setVisibleVideos}
                    setIsVideoDetailsModalOpen={setIsVideoDetailsModalOpen}
                    isPinned={
                      !!pinnedOrderId &&
                      job.orderDetails.order_id === pinnedOrderId
                    }
                    key={`${job.orderDetails.order_id}:${job.orderDetails.videos_selectedFor?.[0]?.video_id ?? "unknown"}`}
                    job={job}
                  />
                ))}
              </div>
            )}
          </div>

          {!showInitialLoading && jobsForCurrentPage.length === 0 && (
            <div className="flex justify-center items-center h-full space-y-4">
              <p className="text-xl text-gray-400 font-semibold text-center my-14">
                {emptyStateMessage}
              </p>
            </div>
          )}

          {jobsDataMeta && (
            <Pagination
              setPage={handlePageChange}
              currentPage={page}
              totalPages={totalPages}
            />
          )}
        </div>
      )}

      <AvailabilityModal
        isOpen={showSameDayAvailabilityModal}
        setOpen={setShowSameDayAvailabilityModal}
      />

      {/* show view details about the jobs modal */}

      <Modal
        setOpen={setIsVideoDetailsModalOpen}
        isOpen={isVideoDetailsModalOpen}
      >
        <ViewDetailsModal
          setIsVideoDetailsModalOpen={setIsVideoDetailsModalOpen}
          data={visibleVideos}
        />
      </Modal>

      {/* <Modal
        width="571px"
        padding="24px"
        setOpen={setIsProjectConfirmModalOpen}
        isOpen={isProjectConfirmModalOpen}
      >
        <ProjectConfirmationModal />
      </Modal> */}
    </div>
  );
};

export default Jobs;

const ViewDetailsModal = ({
  data,
  setIsVideoDetailsModalOpen,
}: {
  setIsVideoDetailsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  data: TOrderVideo[];
}) => {
  return (
    <div className="">
      <div className="flex  items-center justify-between">
        <p className="font-semibold text-lg">Order Video Details</p>

        <button
          className="text-gray-600"
          onClick={() => setIsVideoDetailsModalOpen(false)}
        >
          <X />
        </button>
      </div>

      <div className="pt-2">
        {data.map((item, i) => (
          <DetailsCard key={i} data={item} />
        ))}
      </div>
    </div>
  );
};

const DetailsCard = ({ data }: { data: TOrderVideo }) => {
  return (
    <div className="pb-4 pt-2 border-b border-gray-200 ">
      <h2 className="text-sm font-medium text-gray-600">
        {data.scriptDetails.titleOfTheVideo}
      </h2>

      <div className="flex flex-wrap gap-3 pt-2">
        <Badge value={data.scriptDetails?.videoLength?.label} />
        <Badge value={data.videoEditingDetails?.format} />
        <Badge value={data.scriptDetails?.videoTone.title} />
      </div>
    </div>
  );
};

const Badge = ({ value }: { value: string }) => (
  <p className="bg-[#F2F4F7] w-fit px-2 py-1 rounded-[4px] text-[#788193] text-sm leading-5 tracking-[-2.5%] font-medium whitespace-nowrap">
    {value}
  </p>
);
